import { ApplicationState } from "./../index";
import { DistanceState, DistanceValue } from "../Distance";
import { AddOnState, IAddOn, MapAddonPopups, MapAddonUSP, PrepareAddonTexts, PrepareSelectedAddons, SetAddonVisibility } from "../AddOn";
import { PaymentTypeState, IOptionItem, IPaymentOption } from "../PaymentType";
import { ProfileState } from "../Profile";
import { Texts } from "../ComponentTexts";
import { InsuranceState, Coverage, InsuranceReceiveType, GetCalculatedPrice, CoverageItem } from "../Insurance";
import { PreparePriceList, GetUpdatedFooterPrices, FooterState } from "../Footer";
import { RemoveDuplicates, GetDateFromDateTime } from "../../helperFunctions";
import { DamageFreeYearState } from "../DamageFreeYear";
import { BrandsEnum } from "../../enums";
import { YoungDriverState } from "../YoungDriver";
import { CampaignState } from "../Campaign";
import { DealerPriceState } from "../DealerPrice";
import { USPState } from "../USP";
import WebshopDataDeliveryService from "../../services/WebshopDataDeliveryService";
import { setServerError } from "../error";
import { SERVER_ERROR_MESSAGE, API_HOST_ERROR_MESSAGE } from "../../constants/errorMessages";
import { toErrorPayload } from "../../helpers/errorPayload";
import CarImageService from "../../services/CarImageService";

const method = "POST";
const headers = { "Content-Type": "application/json" };

type Sortable = {
  SortOrder: number;
};

type PaymentTypeInternal = {
  Code: string;
  Name: string;
  IsSelected: boolean;
  BanksList?: IOptionItem[];
  BanksDescription: string;
  Description: string;
  SecondaryText?: string;
  IsAvailable: boolean;
  PaymentPeriods?: IOptionItem[];
};

type InitRequest = {
  WebshopMode: number;
  CarRegistrationNumber: string;
  SocialSecurityNumber: string;

  QuoteNumber?: string;
  Brand?: BrandsEnum;
  CampaignId?: string;
  DisableBuying: boolean;
  DealerPrice?: boolean;
  CalculationMethod?: number;
  CarCode?: string;
  CarModel?: string;
  CarVariant?: string;
  CarYear?: string;
  CarYearTo?: string;
};

const Init = async (state: ApplicationState, dispatch?: Function): Promise<ApplicationState> => {
  if (!state.inputs.apiHost) {
    if (dispatch) dispatch(setServerError(API_HOST_ERROR_MESSAGE, SERVER_ERROR_MESSAGE, state.inputs.errorUrl));
    throw toErrorPayload({ message: API_HOST_ERROR_MESSAGE, header: SERVER_ERROR_MESSAGE, redirectUrl: state.inputs.errorUrl });
  }

  const brand = state.inputs?.partner ?? BrandsEnum.Polestar;
  const url = `${state.inputs.apiHost}/api2/${brand}/newwebshop/Init`;

  const data: InitRequest = {
    WebshopMode: state.inputs.webshopMode,
    CarRegistrationNumber: state.inputs.crn,
    SocialSecurityNumber: state.inputs.ssn,
    CalculationMethod: state.inputs.calculationMethod,
    CarCode: state.inputs.car?.code,
    CarModel: state.inputs.car?.model,
    DisableBuying: state.inputs.disableBuying,
    CarVariant: state.inputs.car?.variant,
    CarYear: state.inputs.car?.yearFrom,
    CarYearTo: state.inputs.car?.yearTo,
    QuoteNumber: state.inputs.quoteNumber,
    Brand: brand,
    CampaignId: state.inputs.campaignId,
    DealerPrice: false,
  };
  
  const carImageResponse = await CarImageService(state.inputs.apiHost, state.inputs.crn, state.inputs.partner);
  const addonPopupResponse = await FetchAddonPopupData(state.inputs.cmsHost, state.inputs.webshopPageGuid);
  const addonUSPResponse = await FetchAddonUSPData(state.inputs.cmsHost, state.inputs.webshopPageGuid);

  const response = await fetch(`${url}`, {
    method,
    mode: "cors",
    headers,
    body: JSON.stringify(data),
  });

  const json = await response.json();

  if (response.status !== 200) {
    const message = (json && json.Message) ?? SERVER_ERROR_MESSAGE;
    if (dispatch) dispatch(setServerError(message, SERVER_ERROR_MESSAGE, state.inputs.errorUrl));
    throw toErrorPayload({ message, header: SERVER_ERROR_MESSAGE, redirectUrl: state.inputs.errorUrl });
  }

  const distance = json.MileageValues as [{ Code: string; Name: string; IsSelected: boolean }];

  // select PaymentsWithPeriods (grouped) and then PeymentTypes as fallback for backwards compatibility
  const paymentTypesFromJson = (json.PaymentTypesWithPeriods || json.PaymentTypes) as PaymentTypeInternal[];

  // TODO: focus on TYPESCRIPT syntax;
  const paymentTypes = paymentTypesFromJson
    .filter(val => !(!val.Code || 0 === val.Code.length))
    .map(
      val =>
      ({
        code: val.Code,
        selected: val.IsSelected,
        available: val.IsAvailable,
        title: val.Name,
        description: val.Description,
        secondaryText: val.SecondaryText,
        bankList: val.BanksList,
        bankHelperText: val.BanksDescription,
        paymentPeriods: val.PaymentPeriods,
        bankCode: val.BanksList?.find(bankItem => bankItem.IsSelected)?.Code,
      } as IPaymentOption),
    );
  const selectedPaymentType = paymentTypes?.find(val => val.selected);
  let selectedPaymentTypeCode: string | undefined;
  if (selectedPaymentType) {
    if (selectedPaymentType.paymentPeriods && selectedPaymentType.paymentPeriods.length > 0) {
      selectedPaymentTypeCode = selectedPaymentType.paymentPeriods.find(x => x.IsSelected)?.Code;
    }
    selectedPaymentTypeCode = selectedPaymentTypeCode ?? selectedPaymentType.code;
  }

  // work with coverages
  const coverages = json.Coverages.sort((l: Sortable, r: Sortable) => l.SortOrder - r.SortOrder) as Coverage[];
  const currentCoverage = coverages.find(x => x.IsRecommended) ?? coverages[0];

  var allCoverageItems: CoverageItem[] = [];
  coverages.forEach(coverage => {
    for (const item of coverage.CoverageItems) {
      if (!allCoverageItems.find(ci => ci.InternalCode === item.InternalCode)) {
        allCoverageItems.push(item);
      }
    }
  });

  const currentCoverageItems = currentCoverage.CoverageItems;

  // campaign
  const campaign = json.Campaign as CampaignState;

  // work with addons
  let addOns = coverages.flatMap(x => x.CoverageAddons).filter(x => x != null) as IAddOn[];
  addOns = addOns?.sort((l: Sortable, r: Sortable) => l.SortOrder - r.SortOrder);

  // distict exclude value.
  const distinct = (value: any, index: any, self: any) => self.indexOf(value) === index;

  addOns.forEach(addon => {
    addon.Exclude = addon.Exclude.filter(distinct);
    addon.campaigned = campaign?.Addons ? campaign.Addons.find(x => x === addon.InternalCode) != null : false;
  });

  addOns = RemoveDuplicates(addOns);
  addOns = PrepareAddonTexts(addOns);
  // for testing purposes added function -> addOns = PrepareAddonMillageQuestion(addOns);
  let selectedAddonInternalCodes = currentCoverage.CoverageAddons?.filter(x => x.Checked)
    ?.map(x => x.InternalCode)
    ?.filter(x => x != null) as string[];
  selectedAddonInternalCodes.push(...(addOns.filter(x => x.campaigned).map(x => x.InternalCode) as string[])); // select campaigned addons by default
  selectedAddonInternalCodes = RemoveDuplicates(selectedAddonInternalCodes);
  addOns = SetAddonVisibility(
    addOns,
    currentCoverage.CoverageAddons?.map(x => x.InternalCode)?.filter(x => x != null) as string[],
    selectedAddonInternalCodes,
  );
  addOns = PrepareSelectedAddons(addOns, selectedAddonInternalCodes);
  addOns = MapAddonPopups(addOns, addonPopupResponse.addonPopupBlocks);
  addOns = MapAddonUSP(addOns, addonUSPResponse.addonUSPBlocks);

  //replace last digits in ssn for security
  // No replace in case of Polestar commercial
  const secureSsn = brand === BrandsEnum.PolestarForetag ? state.inputs.ssn : state.inputs.ssn.replace(/^.*?(\d.*)\d{4}.*?$/gm, "$1****");

  // populate footer prices
  const footerPrices = GetUpdatedFooterPrices(state.footer as FooterState, currentCoverage, selectedAddonInternalCodes);
  coverages.forEach(x => {
    const tmp = PreparePriceList(x, selectedAddonInternalCodes);
    x.CalculatedPrice = tmp[0].price;

    // calculate secondary prices
    x.CalculatedPriceSecondary = GetCalculatedPrice(x.SecondaryPrice, x, selectedAddonInternalCodes);
  });

  // preselect deductible
  let selectedDeductible = currentCoverage?.Deductibles?.find((i: any) => i.IsSelected === true)?.Code;
  if (brand === BrandsEnum.Polestar && !selectedDeductible) {
    selectedDeductible = "2"; //case when polestar has 1 column of coverage without deductibles
  }

  const selectedGlassDeductible = currentCoverage?.GlassDeductibles?.find((i: any) => i.IsSelected === true)?.Code;

  // ReceiveInsuranceTypes
  // show receive insurance type if customer isn't e-customer
  let receiveInsuranceTypes: InsuranceReceiveType | undefined;
  const notECustomer = json.Customer && json.Customer.IsECustomer != null && !json.Customer.IsECustomer;
  if (json.ReceiveInsuranceTypes && notECustomer && brand !== BrandsEnum.Polestar) {
    receiveInsuranceTypes = {
      values: json.ReceiveInsuranceTypes.map((i: any) => ({
        Code: i.Code,
        Name: i.Name,
      })),
      selectedValue: json.ReceiveInsuranceTypes.find((i: any) => i.IsSelected)?.Code,
      title: json.UiContent?.ReceiveInsuranceType?.Header,
      label: {
        title: json.UiContent?.ReceiveInsuranceType?.Header,
      },
      help: {
        title: json.UiContent?.ReceiveInsuranceType?.Popup?.Header,
        description: json.UiContent?.ReceiveInsuranceType?.Popup?.Body,
      },
    };
  }

  return {
    profile: {
      name: json.Customer.Name,
      email: {
        title: json.UiContent?.Email?.Header,
        placeholderText: json.UiContent?.Email?.PlaceHolder,
        state: 0,
        stateDescriptions: [undefined, json.UiContent?.EmailRequiredErrorMessage, json.UiContent?.EmailIncorrectErrorMessage],
        value: "",
      },
      phone: {
        title: json.UiContent?.PhoneNumber?.Header,
        placeholderText: json.UiContent?.PhoneNumber?.PlaceHolder,
        state: 0,
        stateDescriptions: [undefined, json.UiContent?.PhoneRequiredErrorMessage, json.UiContent?.PhoneIncorrectErrorMessage],
        value: "",
      },
      carImageUrl: carImageResponse?.carImageUrl,
      secureSsn: secureSsn,
      age: json.Customer.Age && Number(json.Customer.Age),
      encryptedSSN: json.Customer.EncryptedSSN,
      gender: json.Customer.Gender,
      campaignId: json.Customer.CampaignId,
      encryptedName: json.Customer.EncryptedName,
      isECustomer: json.Customer.IsECustomer,
      cxScriptEnabled: json.CXScriptEnabled,
      portfolio: json.Customer.CarBrandArea,
      isCarNew: json.Customer.IsCarNew,
      isCarVolvoBatteryElectric: json.Customer.IsCarVolvoBatteryElectric,
      isCarBatteryElectric: json.Customer.IsCarBatteryElectric,
      encryptedRegistrationNumber: json.Customer.EncryptedRegistrationNumber,
      carModel: json.Customer.CarModel,
      carYear: Number(json.Customer.CarYear),
      volvoCardNotOwner: json.Customer.VolvoCardNotOwner,
    } as ProfileState,

    addon: {
      addOns: addOns,
      selectedAddOnInternalCodes: selectedAddonInternalCodes,
      title: json.UiContent?.AddonsHeader ?? state.addon?.title,
    } as AddOnState,
    distance: {
      title: json.UiContent?.MileageHeader || "",
      label: {
        title: json.UiContent?.Mileage?.Header,
      },
      help: {
        title: json.UiContent?.Mileage?.Popup?.Header,
        description: json.UiContent?.Mileage?.Popup?.Body,
      },
      selected: distance.find(i => i.IsSelected)?.Code || "",
      values: distance.map<DistanceValue>(i => ({
        title: i.Name,
        value: i.Code,
      })),
    } as DistanceState,
    texts: {
      redirectButtonText: json.UiContent?.ConfirmationRedirectBtnText,
      confirmationHeader: json.UiContent?.ConfirmationHeader,
      confirmationBody: json.UiContent?.ConfirmationBody,
      confirmationExtraInfo: json.UiContent?.ConfirmationExtraInfo,
      confirmationExtraUrl1: {
        urlText: json.UiContent?.ConfirmationExtraUrl1?.Text,
        link: json.UiContent?.ConfirmationExtraUrl1?.Url,
      },
      confirmationExtraUrl2: {
        urlText: json.UiContent?.ConfirmationExtraUrl2?.Text,
        link: json.UiContent?.ConfirmationExtraUrl2?.Url,
      },
      welcomeHeaderPartOneText: (json.UiContent?.WelcomeHeaderPartOneText ?? state.texts?.welcomeHeaderPartOneText)?.trim(),
      // TODO: replace ssn label to orn label in case of Polestar commercial
      ssnHeader: brand === BrandsEnum.PolestarForetag ? json.UiContent?.ORNHeader : json.UiContent?.SSNHeader ?? state.texts?.ssnHeader,
      registrationNumberHeader: json.UiContent?.RegistrationNumberHeader ?? state.texts?.registrationNumberHeader,
      carModelHeader: json.UiContent?.CarModelHeader ?? state.texts?.carModelHeader,
      checkoutButtonText: json.UiContent?.CheckoutButtonText,
      coverageHeader: json.UiContent?.CoverageHeader ?? state.texts?.coverageHeader,
      changeChoiceText: json.UiContent?.ChangeChoiceButtonText ?? state.texts?.changeChoiceText,
      billingSectionHeader: json.UiContent?.BillingHeader ?? state.texts?.billingSectionHeader,
      contactsSectionHeader: json.UiContent?.ContactsHeader ?? state.texts?.contactsSectionHeader,
      trialPeriodMessage: json.UiContent?.Withdrawal.Popup.Header,
      infoBlockText: json.UiContent?.InfoBlockText,
      preliminaryPriceText: json.UiContent?.PreliminaryPriceText,
      antiTheftMessage: json.AntiTheftMessage,
      batteryElectricMessage: json.UiContent?.BatteryElectricTextMessage,
      confirmationLinks: {
        checkoutExtraUrl1: {
          urlText: json.UiContent?.CheckoutExtraUrl1?.Text,
          link: json.UiContent?.CheckoutExtraUrl1?.Url,
        },
        checkoutExtraUrl2: {
          urlText: json.UiContent?.CheckoutExtraUrl2?.Text,
          link: json.UiContent?.CheckoutExtraUrl2?.Url,
        },
      },
      confirmationPromotion1: {
        body: json.UiContent?.ConfirmationPromotion1?.Body,
        header: json.UiContent?.ConfirmationPromotion1?.Header,
        link: {
          text: json.UiContent?.ConfirmationPromotion1?.Link.Text,
          url: json.UiContent?.ConfirmationPromotion1?.Link.Url,
        },
      },
      persona: json.UiContent?.Persona && {
        name: json.UiContent?.Persona?.Name,
        subtitle: json.UiContent?.Persona?.Subtitle,
        imageUrl: json.UiContent?.Persona?.ImageUrl,
      },
      singleRecommendedButton: json.UiContent?.SingleRecomendedCoverageButton,
      kamuxMessage: json.UiContent?.KamuxMessage,
    } as Texts,
    payment: {
      selectedPaymentTypeCode: selectedPaymentTypeCode,
      selectedBankCode: paymentTypes?.find(val => val.selected)?.bankList?.find(val => val.IsSelected)?.Code,
      paymentTypes: paymentTypes,
      bankRequiredErrorMessage: json.UiContent?.BankRequiredErrorMessage,
      sectionHeader: json.UiContent?.PaymentMethod?.Header ?? state.payment?.sectionHeader,
      inputs: {
        bankAccountClearingNumber: {
          title: json.UiContent?.ClearingNumber?.Header,
          placeholderText: json.UiContent?.ClearingNumber?.PlaceHolder,
          state: 0,
          value: "",
          stateDescriptions: [
            undefined,
            json.UiContent?.ClearingNumberRequiredErrorMessage,
            json.UiContent?.ClearingNumberIncorrectErrorMessage,
          ],
          description: {
            text: json.UiContent?.ClearingNumber?.Popup?.Body,
            title: json.UiContent?.ClearingNumber?.Popup?.Header,
          },
        },
        bankAccountNumber: {
          title: json.UiContent?.BankAccount?.Header,
          placeholderText: json.UiContent?.BankAccount?.PlaceHolder,
          state: 0,
          value: "",
          stateDescriptions: [undefined, json.UiContent?.BankAccountRequiredErrorMessage, json.UiContent?.BankAccountIncorrectErrorMessage],
          description: {
            text: json.UiContent?.BankAccount?.Popup?.Body,
            title: json.UiContent?.BankAccount?.Popup?.Header,
          },
        },
        bankAccountOwnerName: {
          title: json.UiContent?.AccountOwnerName?.Header,
          placeholderText: json.UiContent?.AccountOwnerName?.PlaceHolder,
          state: 0,
          value: "",
          stateDescriptions: [
            undefined,
            json.UiContent?.AccountOwnerNameRequiredErrorMessage,
            json.UiContent?.AccountOwnerNameIncorrectErrorMessage,
          ],
          description: {
            text: json.UiContent?.AccountOwnerName?.Popup?.Body,
            title: json.UiContent?.AccountOwnerName?.Popup?.Header,
          },
        },
        bankAccountOwnerSSN: {
          title: json.UiContent?.AccountOwnerSSN?.Header,
          placeholderText: json.UiContent?.AccountOwnerSSN?.PlaceHolder,
          state: 0,
          value: "",
          stateDescriptions: [
            undefined,
            json.UiContent?.AccountOwnerSSNRequiredErrorMessage,
            json.UiContent?.AccountOwnerSSNIncorrectErrorMessage,
          ],
          description: {
            text: json.UiContent?.AccountOwnerSSN?.Popup?.Body,
            title: json.UiContent?.AccountOwnerSSN?.Popup?.Header,
          },
        },
        bankAccountOwner: {
          label: json.UiContent?.AccountOwner,
        },
      },
      digitalSigning: {
        showMessage: json.Customer.ShowSigningMessage,
        message: json.UiContent?.SignedConsentMessage,
      },
    } as PaymentTypeState,

    footer: {
      ...footerPrices,
      totalPriceTooltip: json.UiContent?.TotalPriceTooltipText,
      purchaseButton: json.UiContent?.BuyButtonText,
      expanded: false,
    },

    singleRecomendation: {
      title: json.UiContent?.RecommendationHeaderLabel,
      text: json.UiContent?.RecommendationText,
      coverageItems: currentCoverageItems,
      readMore: {
        text: json.UiContent?.RecommendationReadMoreText,
        linkText: "Läs mer.",
        title: json.UiContent?.RecommendationReadMoreTitle,
      },
    },

    insurance: {
      ...state.insurance,
      avatarEnabled: json.AvatarEnabled,
      coverage: {
        selectedCode: currentCoverage.InternalCode,
        selectedName: currentCoverage.LongName,
        Coverages: coverages,
        CoverageItems: allCoverageItems,
        recommendedTitle: json.UiContent?.RecommendedCoverageLabel ?? state.insurance?.coverage?.recommendedTitle,
        SelectedDeductible: state.inputs.webshopMode === 1 ? selectedDeductible : undefined,
        SelectedGlassDeductible: selectedGlassDeductible,
        IsSingleRecommened: json.SingleRecomendedCoverageEnabled,
        isCarUnder3YearsAndNotImported: Boolean(json.Customer.IsCarUnder3YearsAndNotImported),
        ui: {
          deductible: {
            label: json.UiContent?.Deductible.Header,
            helpHeader: json.UiContent?.Deductible.Popup.Header,
            helpText: json.UiContent?.Deductible.Popup.Body,
          },
          ...(json.UiContent?.GlassDeductible && {
            glassDeductible: {
              label: json.UiContent?.GlassDeductible.Header,
              helpHeader: json.UiContent?.GlassDeductible.Popup.Header,
              helpText: json.UiContent?.GlassDeductible.Popup.Body,
            },
          }),
          coverageSelect: {
            label: json.UiContent?.SelectCoverageHeader,
          },
        },
      },
      startDate: {
        title: json.UiContent.StartDate.Header,
        value: json.StartDate ? GetDateFromDateTime(new Date(json.StartDate)) : undefined,
        helperTextFromUi: json.UiContent.StartDateIncorrectErrorMessage,
        isDisabled: json.Customer.ProductId ? true : false,
      },
      insuranceReceive: receiveInsuranceTypes,
    } as InsuranceState,

    damageFreeYear: {
      ...state.damageFreeYear,
      title: json.UiContent?.TrafficDamage.Header,
      questionVisible: json.Customer.CarHadAccidentsUserCanChoose ?? true,
      selected: json.Customer.CarHadAccidents,
      description: {
        text: json.UiContent?.TrafficDamage.Popup.Body,
        title: json.UiContent?.TrafficDamage.Popup.Header,
      },
    } as DamageFreeYearState,

    youngDriver: {
      ...state.youngDriver,
      title: json.UiContent?.YoungDriver?.Header,
      questionVisible: json.Customer.DisplayDriverAgeQuestion,
      selected: json.Customer.IsDriver25,
      description: {
        text: json.UiContent?.YoungDriver?.Popup.Body,
        title: json.UiContent?.YoungDriver?.Popup.Header,
      },
    } as YoungDriverState,

    campaign: campaign,

    dealerPrice: {
      IsDealerPrice: json.DealerPrice,
      MessageTop: json.DealerPriceMessage,
      MessageTopTitle: json.DealerPriceMessageTitle,
      MessageBottom: json.DealerPriceBottomMessage,
    } as DealerPriceState,

    usp: {
      IsEnabled: json.USP && json.USP.IsEnabled && json.USP.Text && json.USP.Text.length !== 0,
      Header: json.USP.Header,
      Text: json.USP.Text,
    } as USPState,
  } as ApplicationState;
};

export async function FetchAddonPopupData(cmsHost: string, webshopPageGuid: string) {
  return await WebshopDataDeliveryService(cmsHost, "getAddonPopupBlocks", webshopPageGuid);
}

export async function FetchAddonUSPData(cmsHost: string, webshopPageGuid: string) {
  return await WebshopDataDeliveryService(cmsHost, "getAddonUSPBlocks", webshopPageGuid);
}

export default Init;
